import React from 'react';

import { BoxIcon, ChevronDownIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import { PREVENT_AUTOFILL_PROPS } from '@/components/ui/constants';
import { PopoverCombobox } from '@/components/ui/custom/PopoverCombobox';
import { useDeskId } from '@/hooks/useDeskId';
import useDesks from '@/hooks/useDesks';
import { setDeskId } from '@/redux/session/actions';

type DeskDropdownProps = {
  serviceDeskOnly?: boolean;
  variant?: 'default' | 'glossy';
  showSingle?: boolean;
  /**
   * Where to go after the user picks another desk. Pages whose URL holds the desk id must pass it,
   * or the URL keeps the old desk. Without it, the dropdown only updates the selected desk.
   */
  getPath?: (deskId: string) => string;
};

const triggerClassName = {
  default:
    'flex w-full items-center justify-between rounded-md border border-border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  glossy:
    'flex h-10 w-full items-center justify-between rounded px-3 py-2.5 text-sm font-medium text-nav-foreground transition-colors hover:bg-nav-hover',
} as const;

const iconClassName = {
  default: 'mr-2 min-w-4 text-muted-foreground',
  glossy: 'mr-2 min-w-4',
} as const;

const chevronClassName = {
  default: 'ml-2 h-4 w-4 shrink-0 opacity-50',
  glossy: 'ml-2 h-4 w-4 shrink-0',
} as const;

export const DeskDropdown = ({
  serviceDeskOnly = false,
  variant = 'default',
  showSingle = false,
  getPath,
  ref,
}: DeskDropdownProps & { ref?: React.Ref<HTMLDivElement> }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const deskId = useDeskId();
  const { desks } = useDesks();

  const options = desks
    ?.filter((desk) => !serviceDeskOnly || desk.is_service_desk)
    .map((desk) => ({ label: desk.name, value: desk.desk_id }))
    .sort((a, b) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1));

  const handleChange = (value: string) => {
    if (getPath) {
      navigate(getPath(value));
    } else {
      dispatch(setDeskId(value));
    }
  };

  if (!options || (showSingle ? options.length === 0 : options.length <= 1)) {
    return null;
  }

  return (
    <div ref={ref} className="ml-4 flex items-center">
      <PopoverCombobox
        options={options}
        value={deskId || ''}
        onChange={handleChange}
        searchPlaceholderKey="environments.switch_env"
        inputProps={PREVENT_AUTOFILL_PROPS}
        trigger={
          <button
            type="button"
            role="combobox"
            aria-expanded={false}
            aria-controls="popover-combobox-list"
            className={triggerClassName[variant]}
          >
            <BoxIcon size={16} className={iconClassName[variant]} />
            <span className="w-full truncate overflow-hidden text-left font-semibold">
              {options.find((option) => option.value === deskId)?.label || t('environments.no_env')}
            </span>
            <ChevronDownIcon className={chevronClassName[variant]} />
          </button>
        }
      />
    </div>
  );
};
